import type { FirestoreError } from 'firebase/firestore';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../../core/data/usePolledResource';
import { apiList } from '../../../core/data/restClient';

export interface CalendarEntriesState {
  entries: Array<{ id: string; data: TimeEntry }>;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Subscribe to time entries for the calendar's visible date range.
 *
 * When `targetUserId` matches the current user, the server scopes to the
 * owner (safe for any role). When viewing a teammate, the list succeeds
 * only for `rd_manager`. Re-fetches when userId or date range changes.
 */
export function useCalendarEntries(
  targetUserId: string,
  rangeStart: Date,
  rangeEnd: Date,
): CalendarEntriesState {
  const startMs = rangeStart.getTime();
  const endMs = rangeEnd.getTime();
  const { rows, loading, error } = usePolledResource<TimeEntry & { id: string }>(
    targetUserId ? `${targetUserId}:${startMs}:${endMs}` : null,
    () =>
      apiList<TimeEntry & { id: string }>('/timeEntries', {
        userId: targetUserId,
        from: startMs,
        to: endMs,
      }),
  );
  // Server returns startTime DESC; the calendar's SDK contract is ASC.
  return {
    entries: rows.slice().reverse().map((r) => ({ id: r.id, data: r })),
    loading,
    error: error as FirestoreError | null,
  };
}
